/*
  NDIS 0138 Readiness — Source Register v0.2
  ----------------------------------------------------------------------------
  A controlled catalogue of the regulatory and internal sources that the
  consultancy relies on when producing readiness guidance.

  This register is consulted by `lib/sourceVerification.js` to populate the
  Source Verification panel on /results and /report, and the
  "03 / SOURCE VERIFICATION" block in the plain-text and Markdown exports.

  Conventions:
  - `authorityLevel`   : OFFICIAL | FOUNDING_SOURCE | INTERNAL
  - `status`           : CURRENT | REQUIRES_REVIEW | INTERNAL_ONLY | DO_NOT_PUBLISH
  - `lastReviewedDate` : ISO date when the entry was last reviewed by the
                         consultancy. Update this date when the underlying
                         guidance is checked against its current publication.
  - `appliesTo`        : list of internal applicability tags. The source
                         verification helper picks sources whose tags intersect
                         with the current case.
  - `url`              : public URL where the source can be retrieved (omitted
                         for purely internal sources).
  - `confirmedAgainstPublication` : true ONLY when the consultancy has
                         explicitly confirmed the entry against its current
                         published version. Drives the register-driven
                         VERIFIED pathway in `lib/sourceVerification.js`.
                         Never set true speculatively.
  - `confirmationDate` : ISO date of that explicit confirmation
                         (null when unconfirmed).

  IMPORTANT: URLs and dates below MUST be reviewed and confirmed by the
  consultancy on each release. They are recorded here so the report can show
  the source basis transparently — they are not a substitute for opening the
  underlying publication.
*/

export const AUTHORITY_LEVELS = {
  OFFICIAL: "OFFICIAL",
  FOUNDING_SOURCE: "FOUNDING_SOURCE",
  INTERNAL: "INTERNAL"
};

export const SOURCE_STATUS = {
  CURRENT: "CURRENT",
  REQUIRES_REVIEW: "REQUIRES_REVIEW",
  INTERNAL_ONLY: "INTERNAL_ONLY",
  DO_NOT_PUBLISH: "DO_NOT_PUBLISH"
};

// Applicability tags used to select which sources are relevant to a given case.
export const APPLIES_TO = {
  SIL_REGISTRATION: "sil_registration",
  SIL_TRANSITION: "sil_transition",
  ZERO_ONE_THREE_EIGHT_PATHWAY: "0138_pathway",
  SIL_CLAIMS: "sil_claims",
  PAYMENT_CHANGES: "payment_changes",
  JULY_2026_TRANSITION: "july_2026_transition",
  PRACTICE_STANDARDS: "practice_standards",
  EVIDENCE_REQUIREMENTS: "evidence_requirements",
  EXIT_TRANSITION: "exit_transition",
  WIND_DOWN: "wind_down",
  PROGRAM_DEFINITION: "program_definition",
  ZERO_ONE_THREE_EIGHT_READINESS: "0138_readiness",
  OPERATING_LOGIC: "operating_logic",
  CLASSIFIER_RULES: "classifier_rules",
  READINESS_FRAMEWORK: "readiness_framework"
};

export const SOURCES = [
  {
    sourceId: "ndis_commission_mandatory_sil_registration",
    title: "NDIS Commission \u2014 Mandatory registration and transition pathways for supported independent living",
    authorityLevel: AUTHORITY_LEVELS.OFFICIAL,
    sourceType: "regulatory_guidance",
    url: "https://www.ndiscommission.gov.au/about-us/ndis-commission-reform-hub/mandatory-registration/mandatory-registration-SIL",
    lastReviewedDate: "2026-06-29",
    confirmedAgainstPublication: true,
    confirmationDate: "2026-07-05",
    status: SOURCE_STATUS.CURRENT,
    appliesTo: [
      APPLIES_TO.SIL_REGISTRATION,
      APPLIES_TO.SIL_TRANSITION,
      APPLIES_TO.ZERO_ONE_THREE_EIGHT_PATHWAY,
      APPLIES_TO.ZERO_ONE_THREE_EIGHT_READINESS
    ],
    notes: "Primary regulatory reference for the mandatory transition pathway into Registration Group 0138 for SIL providers. Open the published NDIS Commission page on each engagement and confirm the current version of the guidance before relying on this report externally."
  },
  {
    sourceId: "ndia_sil_claims_payment_changes_2026",
    title: "NDIA / NDIS \u2014 SIL claims and payment changes from 1 July 2026",
    authorityLevel: AUTHORITY_LEVELS.OFFICIAL,
    sourceType: "ndia_guidance",
    url: "https://www.ndis.gov.au/news/11576-sil-claims-and-payment-changes-1-july-2026",
    lastReviewedDate: "2026-06-29",
    confirmedAgainstPublication: true,
    confirmationDate: "2026-07-05",
    status: SOURCE_STATUS.CURRENT,
    appliesTo: [
      APPLIES_TO.SIL_CLAIMS,
      APPLIES_TO.PAYMENT_CHANGES,
      APPLIES_TO.JULY_2026_TRANSITION,
      APPLIES_TO.ZERO_ONE_THREE_EIGHT_PATHWAY
    ],
    notes: "Operational claim and payment rules that apply to SIL service delivery from 1 July 2026. Open the current NDIA SIL page and confirm any payment-related claim before relying on it in a client output."
  },
  {
    sourceId: "ndis_commission_sil_practice_standards_supplementary",
    title: "NDIS Commission \u2014 Supplementary module: Supported independent living (SIL) Practice Standards",
    authorityLevel: AUTHORITY_LEVELS.OFFICIAL,
    sourceType: "practice_standards",
    url: "https://www.ndiscommission.gov.au/rules-and-standards/ndis-practice-standards/sil",
    lastReviewedDate: "2026-08-01",
    confirmedAgainstPublication: true,
    confirmationDate: "2026-08-01",
    status: SOURCE_STATUS.CURRENT,
    appliesTo: [
      APPLIES_TO.PRACTICE_STANDARDS,
      APPLIES_TO.EVIDENCE_REQUIREMENTS
    ],
    notes: "Finalised SIL-specific supplementary Practice Standards module published by the NDIS Commission and effective for SIL providers required to be registered from 1 July 2026. Four standards: Supported decision-making, Safeguarding, Practice governance, Agreements about tenancy, housing and support arrangements. Distinct from the broader NDIS Practice Standards reform process, which remains in progress under a separate register entry (`ndis_commission_practice_standards_reform`). Controlled HTML snapshot 2026-08-01 retained in Obsidian archive (not committed to public Git)."
  },
  {
    sourceId: "ndis_commission_practice_standards_reform",
    title: "NDIS Commission \u2014 NDIS Practice Standards reform",
    authorityLevel: AUTHORITY_LEVELS.OFFICIAL,
    sourceType: "practice_standards",
    url: "https://www.ndiscommission.gov.au/about-us/ndis-commission-reform-hub/ndis-practice-standards-reform",
    lastReviewedDate: "2026-08-01",
    confirmedAgainstPublication: true,
    confirmationDate: "2026-08-01",
    status: SOURCE_STATUS.CURRENT,
    gateRole: "advisory",
    appliesTo: [
      APPLIES_TO.READINESS_FRAMEWORK
    ],
    notes: "Broader NDIS Practice Standards review process. Controlled HTML snapshot captured 2026-08-01. This entry is NOT the same as the finalised SIL supplementary Practice Standards module (`ndis_commission_sil_practice_standards_supplementary`). Marked `gateRole: \"advisory\"` so the broader review does not block the SIL-specific VERIFIED gate. Commission webpage wording is explanatory guidance where it differs from enacted law."
  },
  {
    sourceId: "legislation_f2026l00802_amending_instrument",
    title: "NDIS (Provider Registration and Practice Standards) Amendment (Mandatory Registration and Other Matters) Rules 2026 \u2014 F2026L00802",
    authorityLevel: AUTHORITY_LEVELS.OFFICIAL,
    sourceType: "legislation",
    url: "https://www.legislation.gov.au/F2026L00802/asmade",
    lastReviewedDate: "2026-08-01",
    confirmedAgainstPublication: true,
    confirmationDate: "2026-07-27",
    status: SOURCE_STATUS.CURRENT,
    appliesTo: [
      APPLIES_TO.SIL_REGISTRATION,
      APPLIES_TO.ZERO_ONE_THREE_EIGHT_PATHWAY,
      APPLIES_TO.JULY_2026_TRANSITION,
      APPLIES_TO.PRACTICE_STANDARDS
    ],
    notes: "Amending instrument (change history). Registered 24 June 2026; commenced 1 July 2026. Use to verify when and how the SIL definition and Schedule 7A were introduced. Not the current compiled Rules text for day-to-day quotation."
  },
  {
    sourceId: "legislation_f2026c00527_compiled_rules",
    title: "NDIS (Provider Registration and Practice Standards) Rules 2018 \u2014 compiled F2026C00527 C06",
    authorityLevel: AUTHORITY_LEVELS.OFFICIAL,
    sourceType: "legislation",
    url: "https://www.legislation.gov.au/F2018L00631/latest",
    lastReviewedDate: "2026-08-01",
    confirmedAgainstPublication: true,
    confirmationDate: "2026-07-27",
    status: SOURCE_STATUS.CURRENT,
    appliesTo: [
      APPLIES_TO.SIL_REGISTRATION,
      APPLIES_TO.ZERO_ONE_THREE_EIGHT_PATHWAY,
      APPLIES_TO.JULY_2026_TRANSITION,
      APPLIES_TO.PRACTICE_STANDARDS
    ],
    notes: "Current compiled Rules effective 1 July 2026 (F2026C00527, C06). Quote subsection 7(5) from this source when determining whether support falls within registration group 0138. Prefer this over Commission webpage wording for registration determinations."
  },
  {
    sourceId: "ndis_commission_stopping_services",
    title: "NDIS Commission \u2014 Stopping the services you provide",
    authorityLevel: AUTHORITY_LEVELS.OFFICIAL,
    sourceType: "regulatory_guidance",
    url: "https://www.ndiscommission.gov.au/provider-registration/notify-us-changes-or-events/stopping-services-you-provide",
    lastReviewedDate: "2026-06-29",
    confirmedAgainstPublication: true,
    confirmationDate: "2026-07-05",
    status: SOURCE_STATUS.CURRENT,
    appliesTo: [
      APPLIES_TO.EXIT_TRANSITION,
      APPLIES_TO.WIND_DOWN
    ],
    notes: "Source guidance for providers exiting or winding down SIL delivery. Open the current NDIS Commission page and confirm notification obligations before using this report in any exit engagement."
  },
  {
    sourceId: "niss0138_founding_source_netlify",
    title: "Netlify founding source page \u2014 NDIS 0138 Readiness SIL Audit Readiness Program",
    authorityLevel: AUTHORITY_LEVELS.FOUNDING_SOURCE,
    sourceType: "founding_source",
    url: "https://niss-ndis-0138-audit-readiness.netlify.app/",
    lastReviewedDate: "2026-06-29",
    confirmedAgainstPublication: true,
    confirmationDate: "2026-07-05",
    status: SOURCE_STATUS.CURRENT,
    appliesTo: [
      APPLIES_TO.PROGRAM_DEFINITION,
      APPLIES_TO.ZERO_ONE_THREE_EIGHT_READINESS,
      APPLIES_TO.READINESS_FRAMEWORK
    ],
    notes: "Consultancy-authored founding source that defines the NDIS 0138 Readiness SIL Audit Readiness Program. Public-facing, but not an official NDIS Commission publication. Associate Gateway / demonstration surfaces must not be treated as a second product authority."
  },
  {
    sourceId: "niss0138_internal_blueprint_v2",
    title: "Internal Notion operating blueprint \u2014 NDIS 0138 Readiness v2.0",
    authorityLevel: AUTHORITY_LEVELS.INTERNAL,
    sourceType: "internal_blueprint",
    url: "https://app.notion.com/p/a936fc839fe540b586eaa10ef4bfbab1",
    lastReviewedDate: "2026-08-01",
    confirmedAgainstPublication: true,
    confirmationDate: "2026-08-01",
    status: SOURCE_STATUS.INTERNAL_ONLY,
    appliesTo: [
      APPLIES_TO.OPERATING_LOGIC,
      APPLIES_TO.CLASSIFIER_RULES,
      APPLIES_TO.READINESS_FRAMEWORK
    ],
    notes: "Active architecture authority from 27 July 2026. Controls Quote \u2192 Intake \u2192 Assessment \u2192 Report separation, six evidence states, source-governance labels, and provider-matter entities. Not for external distribution as a client claim."
  },
  {
    sourceId: "niss0138_internal_blueprint",
    title: "Internal Notion operating blueprint \u2014 NDIS 0138 Readiness v0.1 (historical)",
    authorityLevel: AUTHORITY_LEVELS.INTERNAL,
    sourceType: "internal_blueprint",
    lastReviewedDate: "2026-06-29",
    confirmedAgainstPublication: false,
    confirmationDate: null,
    status: SOURCE_STATUS.INTERNAL_ONLY,
    appliesTo: [
      APPLIES_TO.OPERATING_LOGIC,
      APPLIES_TO.CLASSIFIER_RULES,
      APPLIES_TO.READINESS_FRAMEWORK
    ],
    notes: "HISTORICAL / SUPERSEDED FOR ARCHITECTURE. Retained as source spine and decision history only. Active architecture authority is `niss0138_internal_blueprint_v2`. Do not rename or delete this historical entry."
  }
];

// Convenience accessor: return all sources whose appliesTo intersects the
// requested tags. If `tags` is empty, returns the full register.
export function getSourcesByApplication(tags = []) {
  if (!tags || tags.length === 0) return SOURCES.slice();
  const set = new Set(tags);
  return SOURCES.filter((s) => s.appliesTo.some((tag) => set.has(tag)));
}
